import { useEffect, useRef, useContext, MouseEvent } from "react";
import { Box, Typography } from "@mui/material";
import { NodeProps } from "@xyflow/react";
import orgChartContext from "src/Pages/Company/OrganizationStructure/context/OrgchartContext";
import useOrgchartStore from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Utils/store";
import { TEmployeeNode } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/CustomReactFlow/Nodes/customNodes.models";
import { useInView, motion } from "motion/react";
import __ from "src/utils/Translations";
import useSearchedNodesStyles from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Utils/useSearchedNodesStyles";
import { Avatar, PresenceIndicator } from "@benefit-systems/hr-tools-ui";
import { Link } from "react-router-dom";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import useIsAhr from "src/utils/hooks/useIsAhr";
import BottomHandle from "./shared-components/BottomHandle";
import TopHandle from "./shared-components/TopHandle";

const EmployeeNode = ({ data, id: nodeId }: NodeProps<TEmployeeNode>) => {
  const ref = useRef(null);
  const isInView = useInView(ref);
  const { edges, setNodeToFocusOn } = useOrgchartStore();
  const { changeNodesVisibility } = useContext(orgChartContext);
  const companyId = useCompanyId();
  const isAhr = useIsAhr();

  const isClickedNodeCollapsed = useOrgchartStore
    .getState()
    .childrenCollapsedStatus.get(nodeId);

  const searchedStyles = useSearchedNodesStyles(nodeId);

  // stop nodes changes, that triggers center node function
  const stopPropagation = (event: MouseEvent) => {
    event.stopPropagation();
    useOrgchartStore.getState().setSearchedNodeId(null);
  };

  const handleClick = () => {
    if (typeof isClickedNodeCollapsed !== "undefined") {
      useOrgchartStore
        .getState()
        .setChildrenCollapsedStatus(
          new Map(useOrgchartStore.getState().childrenCollapsedStatus).set(
            nodeId,
            !useOrgchartStore.getState().childrenCollapsedStatus.get(nodeId)
          )
        );
      setNodeToFocusOn({
        id: nodeId,
        isCollapsed: !isClickedNodeCollapsed,
      });
      changeNodesVisibility({
        isClickedNodeCollapsed,
        clickedNodeId: nodeId,
        edges,
      });
    }
  };

  useEffect(() => {
    useOrgchartStore
      .getState()
      .setVisibleNodesRef(
        new Map(useOrgchartStore.getState().visibleNodesRef).set(
          nodeId,
          isInView
        )
      );
    // eslint-disable-next-line
  }, [isInView]);

  return (
    <div
      ref={ref}
      style={{ opacity: searchedStyles.opacity, cursor: "default" }}
    >
      <TopHandle />
      <motion.div
        layout
        layoutId={`layoutFor${nodeId}`}
        transition={{ duration: 0.2 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.2, delay: 0.4 } }}
        onClick={stopPropagation}
      >
        <Box
          sx={{
            borderRadius: "12px",
            padding: "0",
            width: "340px",
            minWidth: "340px",
            maxWidth: "340px",
            border: searchedStyles.border,
            boxShadow:
              "0px 3px 3px -2px rgba(0, 0, 0, 0.2), 0px 3px 4px 0px rgba(0, 0, 0, 0.14), 0px 1px 8px 0px rgba(0, 0, 0, 0.12);",
          }}
        >
          <Box
            sx={{
              padding: "16px 16px 24px 16px",
              height: data.position ? "auto" : "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: data.position ? "flex-start" : "center",
                gap: "8px",
                height: data.position ? "auto" : "100%",
              }}
            >
              <Box>
                <Link
                  to={
                    isAhr
                      ? `/ahr/employees/${nodeId}`
                      : `/company/edit/${companyId}/employee-management/employees/${nodeId}`
                  }
                  style={{
                    textDecoration: "none",
                  }}
                >
                  <PresenceIndicator status={data?.presenceInformation?.status}>
                    <Avatar size={13} src={data.avatar} alt="ManagerPhoto">
                      {data.initials}
                    </Avatar>
                  </PresenceIndicator>
                </Link>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  lineHeight: "100%",
                }}
              >
                <Link
                  to={
                    isAhr
                      ? `/ahr/employees/${nodeId}`
                      : `/company/edit/${companyId}/employee-management/employees/${nodeId}`
                  }
                  style={{
                    textDecoration: "none",
                  }}
                >
                  <Typography color="text.primary" variant="h5">
                    {data.name}
                  </Typography>
                </Link>
                {data.fk && (
                  <Typography color="textSecondary" variant="body2">
                    (FK {__(`${data.fk}`)})
                  </Typography>
                )}
                {data.position && (
                  <Typography color="textSecondary" variant="body2">
                    {__(`${data.position}`)}
                  </Typography>
                )}
                {data.area &&
                  // TODO remove after BE changes
                  (typeof data.area === "string" ? (
                    <Typography color="textSecondary" variant="body2">
                      {__(`${data.area}`)}
                    </Typography>
                  ) : (
                    <Link
                      to={
                        isAhr
                          ? `/ahr/organization-structure/configure-structure/area/${data.area.id}`
                          : `/company/edit/${companyId}/organization-structure/configure-structure/area/${data.area.id}`
                      }
                      style={{
                        textDecoration: "none",
                      }}
                    >
                      <Typography color="textSecondary" variant="body2">
                        {__(`${data.area.label}`)}
                      </Typography>
                    </Link>
                  ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </motion.div>
      {data.hasChildren && typeof isClickedNodeCollapsed !== "undefined" ? (
        <BottomHandle
          handleClick={handleClick}
          isClickedNodeCollapsed={isClickedNodeCollapsed}
        />
      ) : null}
    </div>
  );
};

export default EmployeeNode;
